/**
 * Central guard: LDS SAVE / PROPOSAL / BOOK — slot vs DOB vs roomSet (A{n} child-as-adult).
 * Does not mutate rooms; pricing / getRoomsSetByCount / XML unchanged.
 */

import {
  enrichLdsPassengerValidationIssues,
  LDS_PASSENGER_ISSUE_TYPES,
} from "./passengers/ldsPassengerValidationIssues";

const LEGACY_FALLBACK_CHILD_AGE_MAX = 12;
const INFANT_MAX_AGE_YEARS_EXCLUSIVE = 2;

const A_ONLY_ROOMSET = /^A(\d+)$/i;

/** User-visible message when validation blocks submit. */
export const LDS_PASSENGERS_VALIDATION_USER_MESSAGE =
  "נמצאה אי התאמה בפרטי הנוסעים. יש לבדוק את גילאי הנוסעים והרכב החדר לפני המשך ההזמנה.";

export class LdsPassengersValidationError extends Error {
  /**
   * @param {string} message
   * @param {{ issues: object[], traces: object[], code?: string }} detail
   */
  constructor(message, detail) {
    super(message);
    this.name = "LdsPassengersValidationError";
    this.code = detail.code ?? "LDS_PASSENGER_OCCUPANCY_MISMATCH";
    this.issues = detail.issues;
    this.traces = detail.traces;
  }
}

/**
 * Resolve child age limits from package childFareExt / explicit max — not hardcoded 11/12.
 *
 * @param {{
 *   childAgeMax?: number,
 *   child_age_max?: number,
 *   childFareExt?: Array<{ ageFrom?: number, ageTo?: number, from?: number, to?: number }>,
 *   child_fare_ext?: unknown,
 *   childAgeRules?: { childAgeMax?: number, childFareExt?: unknown },
 * }} [input]
 */
export function resolveChildAgeLimits(input = {}) {
  const brackets =
    input.childFareExt ??
    input.child_fare_ext ??
    input.childAgeRules?.childFareExt ??
    [];
  let childAgeMax =
    input.childAgeMax ??
    input.child_age_max ??
    input.childAgeRules?.childAgeMax ??
    null;

  if (childAgeMax == null && Array.isArray(brackets) && brackets.length > 0) {
    const tops = brackets
      .map((b) => Number(b?.ageTo ?? b?.to))
      .filter((n) => Number.isFinite(n));
    if (tops.length > 0) {
      childAgeMax = Math.max(...tops);
    }
  }

  const source =
    childAgeMax != null ? "package_child_fare" : "fallback_legacy_no_package_brackets";
  const resolvedMax =
    childAgeMax != null ? Math.floor(Number(childAgeMax)) : LEGACY_FALLBACK_CHILD_AGE_MAX;

  return {
    childAgeMax: resolvedMax,
    adultMinAgeYears: resolvedMax + 1,
    childMaxSlotAgeYears: resolvedMax,
    infantMaxAgeYearsExclusive: INFANT_MAX_AGE_YEARS_EXCLUSIVE,
    source,
  };
}

/**
 * @param {Record<string, unknown>} pax
 * @param {Date} refDate
 * @returns {number|null} full years since birth, or null if unknown / invalid
 */
export function parsePaxAgeYears(pax, refDate = new Date()) {
  if (!pax || typeof pax !== "object") return null;
  const raw =
    pax.birthDate ??
    pax.dateOfBirth ??
    pax.date_of_birth ??
    pax.age;
  if (raw == null || raw === "") return null;

  if (typeof raw === "number" && !Number.isNaN(raw)) {
    const n = Math.floor(raw);
    if (n >= 0 && n <= 120) return n;
    return null;
  }

  const s = String(raw).trim();
  if (!s) return null;

  if (s.includes("/")) {
    const parts = s.split("/").map((x) => x.trim());
    if (parts.length === 3) {
      const d = new Date(`${parts[1]}/${parts[0]}/${parts[2]}`);
      if (Number.isNaN(d.getTime())) return null;
      return yearsBetween(d, refDate);
    }
  }

  const iso = new Date(s);
  if (!Number.isNaN(iso.getTime())) {
    return yearsBetween(iso, refDate);
  }

  return null;
}

function yearsBetween(birth, ref) {
  let years = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) {
    years -= 1;
  }
  return Math.max(0, years);
}

function logChildAsAdultTrace(payload) {
  if (typeof console === "undefined") return;
  try {
    // eslint-disable-next-line no-console
    console[LDS_TRACE_LEVEL(payload.action)](
      "[LDS_BOOK_CHILD_AS_ADULT_TRACE]",
      payload
    );
  } catch {
    /* ignore */
  }
}

function logChildAgeRuleTrace(payload) {
  if (typeof console === "undefined") return;
  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_CHILD_AGE_RULE_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

function LDS_TRACE_LEVEL(action) {
  return action === "block" ? "error" : "info";
}

/**
 * @param {{
 *   roomsAndPaxInfo: unknown,
 *   context?: string,
 *   productType?: string,
 *   referenceDate?: Date,
 *   childAgeMax?: number,
 *   childFareExt?: unknown,
 *   childAgeRules?: object,
 * }} input
 * @returns {{
 *   ok: boolean,
 *   errors: object[],
 *   infos: object[],
 *   traces: object[],
 *   childAgeLimits: ReturnType<typeof resolveChildAgeLimits>,
 * }}
 */
export function validateLdsPassengersConsistency(input) {
  const context = input?.context ?? "unknown";
  const productType = input?.productType ?? "unknown";
  const refDate =
    input?.referenceDate instanceof Date ? input.referenceDate : new Date();
  const roomsAndPaxInfo = input?.roomsAndPaxInfo;
  const childAgeLimits = resolveChildAgeLimits(input ?? {});

  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    logChildAgeRuleTrace({
      context,
      productType,
      ...childAgeLimits,
    });
  }

  const errors = [];
  const infos = [];
  const traces = [];

  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return { ok: true, errors, infos, traces, childAgeLimits };
  }

  roomsAndPaxInfo.forEach((room, roomIndex) => {
    if (!room || typeof room !== "object") {
      traces.push({
        context,
        productType,
        roomIndex,
        roomSet: null,
        adultsCount: 0,
        kidsCount: 0,
        infantsCount: 0,
        totalPax: 0,
        parsedRoomSetTotal: null,
        childAsAdultDetected: false,
        childAgeMax: childAgeLimits.childAgeMax,
        issues: [],
        action: "allow",
      });
      return;
    }

    const roomIssues = [];
    const adults = Array.isArray(room.adults) ? room.adults : [];
    const kids = Array.isArray(room.kids) ? room.kids : [];
    const infants = Array.isArray(room.infants) ? room.infants : [];

    const roomSetRaw = room.room?.roomSet;
    const roomSet =
      roomSetRaw != null && String(roomSetRaw).trim() !== ""
        ? String(roomSetRaw).trim().toUpperCase()
        : "";

    adults.forEach((pax, passengerIndex) => {
      const ageYears = parsePaxAgeYears(pax, refDate);
      if (
        ageYears !== null &&
        ageYears <= childAgeLimits.childMaxSlotAgeYears
      ) {
        const issue = {
          code: "CHILD_IN_ADULT_SLOT",
          issueType: LDS_PASSENGER_ISSUE_TYPES.CHILD_MAPPED_AS_ADULT,
          roomIndex,
          slot: "adults",
          passengerIndex,
          ageYears,
          field: "dateOfBirth",
          childAgeMax: childAgeLimits.childAgeMax,
        };
        errors.push(issue);
        roomIssues.push("CHILD_IN_ADULT_SLOT");
      }
    });

    kids.forEach((pax, passengerIndex) => {
      const ageYears = parsePaxAgeYears(pax, refDate);
      const kidHasData =
        pax &&
        (String(pax.firstname ?? "").trim() ||
          String(pax.lastname ?? "").trim() ||
          String(pax.age ?? pax.birthDate ?? "").trim());
      if (ageYears === null && kidHasData) {
        const issue = {
          code: "MISSING_CHILD_AGE",
          issueType: LDS_PASSENGER_ISSUE_TYPES.MISSING_CHILD_AGE,
          roomIndex,
          slot: "kids",
          passengerIndex,
          field: "age",
          childAgeMax: childAgeLimits.childAgeMax,
        };
        errors.push(issue);
        roomIssues.push("MISSING_CHILD_AGE");
        return;
      }
      if (ageYears > childAgeLimits.childMaxSlotAgeYears) {
        const issue = {
          code: "ADULT_IN_CHILD_SLOT",
          issueType: LDS_PASSENGER_ISSUE_TYPES.ADULT_MAPPED_AS_CHILD,
          roomIndex,
          slot: "kids",
          passengerIndex,
          ageYears,
          field: "dateOfBirth",
          childAgeMax: childAgeLimits.childAgeMax,
        };
        errors.push(issue);
        roomIssues.push("ADULT_IN_CHILD_SLOT");
      }
    });

    infants.forEach((pax, passengerIndex) => {
      const ageYears = parsePaxAgeYears(pax, refDate);
      if (ageYears !== null && ageYears >= childAgeLimits.infantMaxAgeYearsExclusive) {
        const issue = {
          code: "NON_INFANT_IN_INFANT_SLOT",
          issueType: LDS_PASSENGER_ISSUE_TYPES.UNSUPPORTED_CHILD_AGE,
          roomIndex,
          slot: "infants",
          passengerIndex,
          ageYears,
          field: "dateOfBirth",
        };
        errors.push(issue);
        roomIssues.push("NON_INFANT_IN_INFANT_SLOT");
      }
    });

    const adultsCount = adults.length;
    const kidsCount = kids.length;
    const infantsCount = infants.length;
    const occupancyPax = adultsCount + kidsCount;
    const totalPax = occupancyPax + infantsCount;

    let parsedRoomSetTotal = null;

    const rsMatch = roomSet.match(A_ONLY_ROOMSET);
    if (rsMatch) {
      parsedRoomSetTotal = parseInt(rsMatch[1], 10);
      if (
        Number.isFinite(parsedRoomSetTotal) &&
        occupancyPax !== parsedRoomSetTotal
      ) {
        const issue = {
          code: "ROOMSET_TOTAL_PAX_MISMATCH",
          roomIndex,
          roomSet,
          totalPax,
          occupancyPax,
          adultsCount,
          kidsCount,
          infantsCount,
          parsedRoomSetTotal,
          field: "roomComposition",
          expectedAdults: kidsCount === 0 ? parsedRoomSetTotal : undefined,
          actualAdults: adultsCount,
          expectedChildren: undefined,
          actualChildren: kidsCount,
        };
        errors.push(issue);
        roomIssues.push("ROOMSET_TOTAL_PAX_MISMATCH");
      }
    }

    const childAsAdultPattern =
      Boolean(rsMatch) &&
      kidsCount > 0 &&
      Number.isFinite(parsedRoomSetTotal) &&
      occupancyPax === parsedRoomSetTotal;
    const childAsAdultDetected = childAsAdultPattern;

    const hasRoomError = errors.some((e) => e.roomIndex === roomIndex);

    if (childAsAdultPattern && !hasRoomError) {
      infos.push({
        code: "CHILD_AS_ADULT_OK",
        roomIndex,
        roomSet,
        adultsCount,
        kidsCount,
        totalPax,
        parsedRoomSetTotal,
      });
      roomIssues.push("CHILD_AS_ADULT_OK");
    }

    const action = hasRoomError
      ? "block"
      : childAsAdultDetected
        ? "CHILD_AS_ADULT_OK"
        : "allow";

    const tracePayload = {
      context,
      productType,
      roomIndex,
      roomSet: roomSet || null,
      adultsCount,
      kidsCount,
      infantsCount,
      totalPax,
      parsedRoomSetTotal,
      childAsAdultDetected,
      childAgeMax: childAgeLimits.childAgeMax,
      childAgeSource: childAgeLimits.source,
      issues: [...new Set(roomIssues)],
      action,
    };
    traces.push(tracePayload);
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      logChildAsAdultTrace(tracePayload);
    }
  });

  const ok = errors.length === 0;
  const structuredIssues = enrichLdsPassengerValidationIssues(
    errors,
    roomsAndPaxInfo,
    { childAgeLimits, referenceDate: refDate }
  );

  return {
    ok,
    errors,
    structuredIssues,
    infos,
    traces,
    childAgeLimits,
  };
}

export { LDS_PASSENGER_ISSUE_TYPES } from "./passengers/ldsPassengerValidationIssues";
